import { ApprovalsBadge } from '@/features/approvals/approvals-badge';
import { APPROVER_PERMISSIONS } from '@/features/approvals/approvers';
import {
  Building2,
  ClipboardCheck,
  DoorOpen,
  LayoutDashboard,
  Network,
  ScrollText,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
  UsersRound,
} from 'lucide-react';
import {
  DataGapsWidget,
  HeadcountWidget,
  MovementsWidget,
  MyProfileWidget,
  MyTeamWidget,
  PayrollWidget,
  PendingApprovalsWidget,
} from '@/features/dashboard/widgets';
import { payrollModule } from './payroll';
import { timeModule } from './time';
import type { AppModule } from './registry';

/** Core HR modules (DESIGN_SYSTEM §5 sidebar groups). */
export const coreModules: readonly AppModule[] = [
  {
    id: 'me',
    label: 'Me',
    nav: [
      { id: 'dashboard', label: 'Dashboard', to: '/', icon: LayoutDashboard },
      {
        id: 'my-profile',
        label: 'My profile',
        to: '/me',
        icon: UserRound,
        requiredPermissions: ['self.read'],
      },
      {
        // The unified inbox (ARCHITECTURE §7): managers decide leave, HR Bank & TIN changes and
        // leave of people without a manager, payroll approvers runs.
        id: 'approvals',
        label: 'Approvals',
        to: '/approvals',
        icon: ClipboardCheck,
        requiredPermissions: APPROVER_PERMISSIONS,
        orHasReports: true,
        badge: ApprovalsBadge,
      },
    ],
    widgets: [
      {
        id: 'my-profile',
        title: 'My profile',
        component: MyProfileWidget,
        requiredPermissions: ['self.read'],
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    nav: [
      {
        id: 'my-team',
        label: 'My team',
        to: '/team',
        icon: UsersRound,
        requiredPermissions: ['team.read'],
        requiresReports: true,
      },
    ],
    widgets: [
      {
        id: 'my-team',
        title: 'My team',
        component: MyTeamWidget,
        requiredPermissions: ['team.read'],
        requiresReports: true,
      },
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    nav: [
      {
        id: 'employees',
        label: 'Employees',
        to: '/employees',
        icon: Users,
        requiredPermissions: ['employee.read.all'],
      },
      {
        id: 'exits',
        label: 'Exits',
        to: '/hr/exits',
        icon: DoorOpen,
        requiredPermissions: ['employee.update.all'],
      },
      {
        id: 'org-chart',
        label: 'Org chart',
        to: '/hr/org-chart',
        icon: Network,
        requiredPermissions: ['employee.read.all'],
      },
      {
        id: 'departments',
        label: 'Departments & designations',
        to: '/hr/departments',
        icon: Building2,
        requiredPermissions: ['lookup.manage'],
      },
    ],
    widgets: [
      {
        id: 'headcount',
        title: 'Headcount',
        component: HeadcountWidget,
        requiredPermissions: ['employee.read.all'],
      },
      {
        id: 'payroll',
        title: 'Payroll today',
        component: PayrollWidget,
        requiredPermissions: ['salary.read.all'],
      },
      {
        id: 'movements',
        title: 'Joiners & leavers this month',
        component: MovementsWidget,
        requiredPermissions: ['employee.read.all'],
      },
      {
        id: 'approvals',
        title: 'Pending approvals',
        component: PendingApprovalsWidget,
        requiredPermissions: ['bank.approve'],
      },
      {
        id: 'data-gaps',
        title: 'Data completeness',
        component: DataGapsWidget,
        requiredPermissions: ['employee.update.all'],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    nav: [
      {
        id: 'users',
        label: 'Users & roles',
        to: '/admin/users',
        icon: ShieldCheck,
        requiredPermissions: ['role.manage'],
      },
      {
        id: 'audit',
        label: 'Audit log',
        to: '/admin/audit',
        icon: ScrollText,
        requiredPermissions: ['audit.read'],
      },
      {
        id: 'settings',
        label: 'Settings',
        to: '/admin/settings',
        icon: Settings,
        requiredPermissions: ['settings.manage'],
      },
    ],
  },
];

/**
 * Registered modules, in sidebar order: Core groups, then module groups (Payroll, Time & Leave),
 * with Administration last.
 */
export const appModules: readonly AppModule[] = [
  ...coreModules.filter((m) => m.id !== 'admin'),
  payrollModule,
  timeModule,
  ...coreModules.filter((m) => m.id === 'admin'),
];
